<template>
  <div class="page">
    <!-- 顶部导航栏：返回按钮 + 标题（样式与其他子页面一致） -->
    <div class="nav-bar">
      <div class="nav-back" @click="goBack">
        <!-- 返回箭头 -->
        <svg viewBox="0 0 24 24">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </div>
      <div class="nav-title">{{ sourceLabel }}号码</div>
    </div>

    <!-- 彩票类型切换标签：默认快乐8 -->
    <div class="type-tabs">
      <div
        v-for="t in typeList"
        :key="t.value"
        class="type-tab"
        :class="{ active: lotteryType === t.value }"
        @click="switchType(t.value)"
      >
        {{ t.label }}
      </div>
    </div>

    <!-- 选择区域 -->
    <div class="content">
      <!-- 购买期号选择：数据来自后端“可投注期号”接口，切换彩票类型时重新查询 -->
      <div class="section">
        <div class="section-title">选择购买期号</div>
        <div class="issue-select-wrap">
          <select
            v-model="betNumIssue"
            class="issue-select"
            :disabled="issueLoading"
          >
            <!-- 加载中占位选项 -->
            <option v-if="issueLoading" value="">期号加载中...</option>
            <!-- 每个可购买期号一个选项，显示期号和开奖时间 -->
            <option
              v-for="item in issueList"
              :key="item.betNumIssue"
              :value="String(item.betNumIssue)"
            >
              第 {{ item.betNumIssue }} 期（{{ formatIssueTime(item.drawNumTime) }} 开奖）
            </option>
          </select>
        </div>
        <!-- 没有可购买期号时提示 -->
        <div v-if="!issueLoading && !issueList.length" class="issue-empty">
          当前没有可购买的期号，请稍后再试
        </div>
      </div>

      <!-- 组合模式选择：折叠框。点标题展开/收起，选中玩法后自动收起，标题右侧回显当前选择 -->
      <div class="section">
        <div class="collapse-head" @click="toggleModePanel">
          <span class="section-title">选择号码组合模式</span>
          <span class="collapse-sum">
            <template v-if="playType">{{ playType }} · {{ modeDesc(playType) }}</template>
            <span v-else class="collapse-placeholder">请选择</span>
            <!-- 箭头：展开时旋转 180 度 -->
            <svg class="collapse-arrow" :class="{ open: modePanelOpen }" viewBox="0 0 24 24">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </div>

        <!-- 折叠内容区：max-height 过渡动画实现展开/收起 -->
        <div class="collapse-body" :class="{ open: modePanelOpen }">
          <!-- 选项按钮：一行放多个，选中的高亮 -->
          <div v-if="currentModes.length" class="mode-grid">
            <div
              v-for="m in currentModes"
              :key="m"
              class="mode-item"
              :class="{ active: playType === m }"
              @click="selectMode(m)"
            >
              <div class="mode-value">{{ m }}</div>
              <div class="mode-desc">{{ modeDesc(m) }}</div>
            </div>
          </div>

          <!-- 暂无组合模式的类型（如七乐彩，后续补充） -->
          <div v-else class="mode-empty">该彩票类型组合模式暂未开放，敬请期待</div>
        </div>
      </div>

      <!-- 手动输入区域：仅手动输入方式显示，每个号码一个独立小方框 -->
      <div v-if="isManual && currentModes.length" class="section">
        <div class="section-title">手动填写号码</div>

        <!-- 号码方框区：支持在任意方框粘贴整串号码自动分配 -->
        <div class="boxes-panel" @paste="onPasteFill">
          <!-- 主号码组：快乐8号码/双色球红球/大乐透前区，红色描边 -->
          <div v-if="mainGroupLabel" class="group-label">{{ mainGroupLabel }}</div>
          <div class="boxes-row">
            <input
              v-for="i in mainCount"
              :key="'m' + i"
              ref="mainInputs"
              :value="mainArr[i - 1]"
              type="text"
              inputmode="numeric"
              :maxlength="boxMaxLen"
              class="num-box red"
              @input="onBoxInput('main', i - 1, $event)"
              @keydown.delete="onBoxBackspace('main', i - 1, $event)"
              @keyup.enter="handleGenerate"
              @focus="onBoxFocus($event)"
            />
          </div>

          <!-- 辅助号码组：双色球蓝球/大乐透后区，蓝色描边，前面加 + 号 -->
          <template v-if="extraCount">
            <div class="extra-split">
              <span class="split-plus">+</span>
              <span class="group-label">{{ extraGroupLabel }}</span>
            </div>
            <div class="boxes-row">
              <input
                v-for="i in extraCount"
                :key="'e' + i"
                ref="extraInputs"
                :value="extraArr[i - 1]"
                type="text"
                inputmode="numeric"
              :maxlength="boxMaxLen"
              class="num-box blue"
                @input="onBoxInput('extra', i - 1, $event)"
                @keydown.delete="onBoxBackspace('extra', i - 1, $event)"
                @keyup.enter="handleGenerate"
                @focus="onBoxFocus($event)"
              />
            </div>
          </template>
        </div>

        <!-- 填写说明：根据当前类型和组合模式动态提示需要填几个、取值范围 -->
        <div class="manual-tip">{{ manualTip }}</div>
      </div>

      <!-- 提交按钮：随机方式显示“出号”，手动方式显示“提交”；请求中或缺少必要选择时禁止点击 -->
      <button
        class="generate-btn"
        :disabled="loading || !currentModes.length || !betNumIssue"
        @click="handleGenerate"
      >
        {{ loading ? '提交中...' : (isManual ? '提 交' : '出 号') }}
      </button>
    </div>

    <!-- 模型推荐方式专属：下方展示全部类型"待兑奖"的推荐号码列表（独立组件，接口同推荐列表，多传 recommendNumStatus=pending） -->
    <div v-if="isModel" class="model-rec-section">
      <div class="model-rec-title">待兑奖推荐号码</div>
      <RecommendPendingBetNumListView />
    </div>

    <!-- ===== 投注成功弹窗：提交成功后弹出，回显刚才的选择和（手动方式的）号码 ===== -->
    <div v-if="showResult" class="modal-mask" @click.self="closeResult">
      <div class="modal-box">
        <!-- 弹窗头部 -->
        <div class="modal-head">
          <span class="modal-title">投注成功</span>
          <!-- 右上角关闭叉号 -->
          <span class="modal-close" @click="closeResult">×</span>
        </div>

        <!-- 回显信息：全部取自投注接口返回结果 result -->
        <div class="modal-info">
          <div class="info-row">
            <span class="info-label">彩票类型</span>
            <span class="info-value">{{ resultTypeLabel }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">购买期号</span>
            <span class="info-value">第 {{ result && result.betNumIssue ? result.betNumIssue : '' }} 期</span>
          </div>
          <div class="info-row">
            <span class="info-label">玩法</span>
            <span class="info-value">{{ result ? result.playType : '' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">出号方式</span>
            <span class="info-value">{{ resultSourceLabel }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">开奖时间</span>
            <span class="info-value">{{ result && result.betNumDrawTime ? result.betNumDrawTime : '' }}</span>
          </div>
        </div>

        <!-- 号码区域：从返回结果 result.betNumDTOs 取号码展示（随机/手动均返回） -->
        <div class="modal-nums">
          <template v-if="result && result.betNumDTOs && result.betNumDTOs.length">
            <div v-for="(dto, i) in result.betNumDTOs" :key="i" class="num-line">
              <!-- 第几注 -->
              <div class="line-label">第 {{ i + 1 }} 注</div>
              <!-- 号码球：redNum 红球，blueNum 蓝球 -->
              <div class="line-balls">
                <span
                  v-for="(n, j) in mainNums(dto)"
                  :key="'m' + j"
                  class="num-ball"
                >{{ n }}</span>
                <template v-if="extraNums(dto).length">
                  <span class="num-plus">+</span>
                  <span
                    v-for="(n, j) in extraNums(dto)"
                    :key="'e' + j"
                    class="num-ball blue"
                  >{{ n }}</span>
                </template>
              </div>
            </div>
          </template>
          <!-- 兜底提示：返回结果不含号码时 -->
          <div v-else class="random-tip">
            号码已生成，可在「号码管理 - 投注号码列表」中查看
          </div>
        </div>

        <!-- 弹窗底部操作 -->
        <div class="modal-foot">
          <!-- 有号码时显示复制按钮，点击复制全部号码 -->
          <button
            v-if="result && result.betNumDTOs && result.betNumDTOs.length"
            class="foot-btn primary"
            @click="copyNums"
          >复制号码</button>
          <button class="foot-btn plain" @click="closeResult">关 闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 导入可投注期号查询接口、投注接口
import { getAllowBetNumInfo, betNum } from '@/api/lottery'
// 彩票类型枚举统一从工具文件引入
import { lotteryTypeList, lotteryTypeLabel, lotteryModeMap, plsModeDescMap } from '@/utils/lottery'
// 待兑奖推荐号码列表组件：模型推荐方式下方展示（全部类型、状态 pending）
import RecommendPendingBetNumListView from './list/RecommendPendingBetNumListView.vue'

export default {
  name: 'BetNumView',
  components: {
    RecommendPendingBetNumListView
  },
  data() {
    return {
      // 当前选中的彩票类型，默认双色球（请求参数名 lotteryType）
      lotteryType: 'ssq',
      // 彩票类型枚举（统一维护在 @/utils/lottery）
      typeList: lotteryTypeList,
      // 各彩票类型支持的组合模式（统一维护在 @/utils/lottery）
      modeMap: lotteryModeMap,
      // 当前选中的玩法/组合模式，默认快乐8双色球的 '7+1'（请求参数名 playType）
      playType: '7+1',
      // 组合模式折叠面板是否展开：默认收起，点击标题后展开
      modePanelOpen: false,
      // 号码来源：从上个页面带入，random=随机生成 / manual=手动输入，默认随机
      // （请求参数名 betNumSource）
      betNumSource: this.$route.query.source || 'random',
      // ===== 购买期号下拉框数据 =====
      issueList: [],        // 后端返回的可购买期号列表 [{ betNumIssue, drawNumTime }]
      issueLoading: false,  // 期号列表是否加载中
      betNumIssue: '',      // 当前选中的购买期号（字符串，如 "2026264"）
      loading: false,       // 是否正在请求投注接口
      showResult: false,    // 是否展示成功弹窗
      result: null,         // 投注成功后接口返回的结果对象（弹窗回显用）
      // 手动输入：每个小方框一个号码，按下标对应
      mainArr: [],           // 主号码组（快乐8号码/红球/前区）
      extraArr: []           // 辅助号码组（蓝球/后区），快乐8时为空数组
    }
  },
  computed: {
    // 是否为手动输入方式（随机方式下不显示手动填写区）
    isManual() {
      return this.betNumSource === 'manual'
    },
    // 是否为模型推荐方式（模型推荐下方展示待兑奖的推荐号码列表）
    isModel() {
      return this.betNumSource === 'model'
    },
    // 主号码组需要填几个：快乐8取玩法数值，双色球/大乐透取 + 前面的数，排列三取 + 后面的数
    mainCount() {
      const mode = this.playType
      if (!mode) return 0
      // 排列三：玩法形如 direct+3，加号后面的数字就是号码个数（3=单式，4=复式）
      if (this.lotteryType === 'pls') {
        return Number(mode.split('+')[1]) || 0
      }
      return Number(mode.indexOf('+') > -1 ? mode.split('+')[0] : mode)
    },
    // 辅助号码组需要填几个：双色球/大乐透取 + 后面的数；排列三的 + 是玩法名一部分，没有辅助组
    extraCount() {
      const mode = this.playType
      if (!mode) return 0
      if (this.lotteryType === 'pls') return 0
      return mode.indexOf('+') > -1 ? Number(mode.split('+')[1]) : 0
    },
    // 每个方框最多填几位：排列三每位就是一个数字（0-9）只填 1 位，其他类型最多 2 位
    boxMaxLen() {
      return this.lotteryType === 'pls' ? 1 : 2
    },
    // 号码最小值：排列三每位可以是 0，其他类型号码从 1 开始
    numMin() {
      return this.lotteryType === 'pls' ? 0 : 1
    },
    // 同组号码是否允许重复：排列三直选/组选3允许重复（组选6必须互不相同），其他类型不允许
    allowRepeatNums() {
      if (this.lotteryType === 'pls') {
        return this.playType.indexOf('group6') !== 0
      }
      return false
    },
    // 主号码组名称（快乐8不显示组名）
    mainGroupLabel() {
      const map = { ssq: '红球', dlt: '前区' }
      return map[this.lotteryType] || ''
    },
    // 辅助号码组名称
    extraGroupLabel() {
      const map = { ssq: '蓝球', dlt: '后区' }
      return map[this.lotteryType] || ''
    },
    // 当前彩票类型对应的组合模式列表
    currentModes() {
      return this.modeMap[this.lotteryType] || []
    },
    // 出号方式中文名（导航栏标题用，基于当前页面选择）
    sourceLabel() {
      const map = { random: '随机生成', manual: '手动输入', model: '模型推荐' }
      return map[this.betNumSource] || this.betNumSource
    },
    // 弹窗回显-彩票类型中文名（取自返回结果 result.lotteryType）
    resultTypeLabel() {
      return this.result ? lotteryTypeLabel(this.result.lotteryType) : ''
    },
    // 弹窗回显-出号方式中文名（取自返回结果 result.betNumSource）
    resultSourceLabel() {
      if (!this.result) return ''
      const map = { random: '随机生成', manual: '手动输入', model: '模型推荐' }
      return map[this.result.betNumSource] || this.result.betNumSource
    },
    // 当前选中期号对应的开奖时间（投注接口 betNumDrawTime 参数）
    // 数据来源：getAllowBetNumInfo 返回的 issueList 中匹配 betNumIssue 的 drawNumTime
    betNumDrawTime() {
      if (!this.betNumIssue || !this.issueList.length) return ''
      const item = this.issueList.find(o => String(o.betNumIssue) === String(this.betNumIssue))
      return item ? item.drawNumTime : ''
    },
    // 手动填写区下方的填写说明：根据类型 + 玩法动态生成
    manualTip() {
      if (!this.isManual) return ''
      const mode = this.playType
      // 快乐8：模式值就是要填的号码个数，范围 1-80
      if (this.lotteryType === 'klb') {
        return `请在下面 ${mode} 个方框中各填一个号码（范围 1-80，号码不可重复），填满两位数字会自动跳到下一个框`
      }
      // 七乐彩：30选7，范围 1-30
      if (this.lotteryType === 'qlc') {
        return `请在下面 ${mode} 个方框中各填一个号码（范围 1-30，号码不可重复），填满两位数字会自动跳到下一个框`
      }
      // 排列三：每个方框填 1 位数字（0-9），直选/组选3可重复，组选6不可重复
      if (this.lotteryType === 'pls') {
        const count = mode.split('+')[1] || ''
        const repeatTip = mode.indexOf('group6') === 0 ? '号码不可重复' : '允许重复'
        return `${plsModeDescMap[mode] || mode}：请在下面 ${count} 个方框中各填 1 位数字（0-9，${repeatTip}），填满 1 位会自动跳到下一个框`
      }
      // 双色球 / 大乐透：模式形如 6+1，加号前是红/前区个数，加号后是蓝/后区个数
      if (mode.indexOf('+') > -1) {
        const parts = mode.split('+')
        if (this.lotteryType === 'ssq') {
          return `红框填 ${parts[0]} 个红球（1-33），蓝框填 ${parts[1]} 个蓝球（1-16），同组内号码不可重复`
        }
        if (this.lotteryType === 'dlt') {
          return `红框填 ${parts[0]} 个前区号码（1-35），蓝框填 ${parts[1]} 个后区号码（1-12），同组内号码不可重复`
        }
      }
      return '请在方框中填写号码'
    }
  },
  // 页面创建后：初始化空方框，并查询默认类型（快乐8）的可购买期号
  created() {
    this.initBoxes()
    this.fetchIssueList()
  },
  watch: {
    // 切换玩法后，方框数量要重新生成（切换彩票类型时玩法会在 switchType 里重置）
    playType() {
      this.initBoxes()
    }
  },
  methods: {
    // 按当前组合模式初始化方框数组（每个元素是对应方框里的字符串）
    initBoxes() {
      this.mainArr = new Array(this.mainCount).fill('')
      this.extraArr = new Array(this.extraCount).fill('')
    },

    // 小方框输入事件：只保留数字、最多两位（排列三只留一位），写回数组（用 $set 保证 Vue2 响应式）
    // 填满后自动跳到下一个方框，减少手动点击
    onBoxInput(group, index, e) {
      // type="text" 也可能被输入字母/符号，这里强制过滤，只留数字
      const val = e.target.value.replace(/\D/g, '').slice(0, this.boxMaxLen)
      const arrName = group === 'main' ? 'mainArr' : 'extraArr'
      this.$set(this[arrName], index, val)
      // 填满本框（排列三 1 位、其他类型 2 位）自动跳到下一个框
      if (val.length === this.boxMaxLen) {
        const count = group === 'main' ? this.mainCount : this.extraCount
        if (index + 1 < count) {
          // 本组还有下一个框：跳本组下一个
          this.focusBox(group, index + 1)
        } else if (group === 'main' && this.extraCount) {
          // 主号码组已填完且还有蓝球组：跳到蓝球/后区组第一个框
          this.focusBox('extra', 0)
        }
      }
    },

    // 退格键：当前框为空时，自动跳回上一个框继续删除
    onBoxBackspace(group, index) {
      const arr = group === 'main' ? this.mainArr : this.extraArr
      if (arr[index]) return // 当前框有内容时走浏览器默认删除，不拦截
      if (index > 0) {
        this.focusBox(group, index - 1)
      } else if (group === 'extra') {
        // 蓝球/后区组第一个框为空：退格跳回主号码组最后一个框
        this.focusBox('main', this.mainCount - 1)
      }
    },

    // 聚焦某个方框并选中已有内容，方便直接覆盖输入
    focusBox(group, index) {
      const refs = this.$refs[group === 'main' ? 'mainInputs' : 'extraInputs']
      if (refs && refs[index]) {
        refs[index].focus()
        refs[index].select()
      }
    },

    // 聚焦时全选框内文字
    onBoxFocus(e) {
      e.target.select()
    },

    // 粘贴填充：在任意方框粘贴整串号码（如 13,15,29,... 或 1,2,3+4），自动分配到各方框
    onPasteFill(e) {
      e.preventDefault() // 阻止默认只粘到当前框的行为
      const text = (e.clipboardData || window.clipboardData).getData('text') || ''
      if (!text) return

      // 排列三：粘贴 "120" 或 "1,2,0" 都按单个数字拆开，逐位分配到方框
      if (this.lotteryType === 'pls') {
        this.fillGroup('main', text.replace(/\D/g, '').split(''))
        this.$nextTick(() => {
          const emptyMain = this.mainArr.findIndex(v => !v)
          if (emptyMain > -1) this.focusBox('main', emptyMain)
        })
        return
      }

      // 有 + 号：按红蓝两组分别提取数字
      if (this.extraCount && text.indexOf('+') > -1) {
        const parts = text.split('+')
        this.fillGroup('main', parts[0].match(/\d+/g) || [])
        this.fillGroup('extra', (parts[1] || '').match(/\d+/g) || [])
      } else {
        // 没有 + 号：数字先填满主号码组，剩余的填入辅助组
        const nums = text.match(/\d+/g) || []
        this.fillGroup('main', nums.slice(0, this.mainCount))
        if (this.extraCount) {
          this.fillGroup('extra', nums.slice(this.mainCount, this.mainCount + this.extraCount))
        }
      }

      // 粘贴完把光标定位到第一个还没填的框
      this.$nextTick(() => {
        const emptyMain = this.mainArr.findIndex(v => !v)
        if (emptyMain > -1) {
          this.focusBox('main', emptyMain)
        } else if (this.extraCount) {
          const emptyExtra = this.extraArr.findIndex(v => !v)
          if (emptyExtra > -1) this.focusBox('extra', emptyExtra)
        }
      })
    },

    // 用一组号码填充指定方框组（超出的忽略，不足的留空）
    fillGroup(group, nums) {
      const arrName = group === 'main' ? 'mainArr' : 'extraArr'
      const count = group === 'main' ? this.mainCount : this.extraCount
      for (let i = 0; i < count; i++) {
        this.$set(this[arrName], i, nums[i] ? String(nums[i]).slice(0, this.boxMaxLen) : '')
      }
    },

    // 切换彩票类型：玩法默认选中该类型第一项（面板保持收起），并重新查询期号
    switchType(value) {
      if (this.lotteryType === value) return
      this.lotteryType = value
      const modes = this.modeMap[value] || []
      this.playType = modes.length ? modes[0] : ''
      this.modePanelOpen = false
      this.fetchIssueList()
    },

    // 展开/收起组合模式折叠面板（暂未开放的类型不响应）
    toggleModePanel() {
      if (!this.currentModes.length) return
      this.modePanelOpen = !this.modePanelOpen
    },

    // 选择某个玩法：赋值后自动收起面板
    selectMode(m) {
      this.playType = m
      this.modePanelOpen = false
    },

    // 查询当前彩票类型可购买的期号列表，成功后默认选中第一个期号
    async fetchIssueList() {
      this.issueLoading = true
      // 切换类型时先清空旧选择，避免把上期类型的期号提交出去
      this.betNumIssue = ''
      this.issueList = []
      try {
        const res = await getAllowBetNumInfo({ lotteryType: this.lotteryType })
        if (res.code === '200' || res.success) {
          this.issueList = res.data || []
          // 默认选中第一个可购买期号（下拉框 value 用字符串，和接口要求一致）
          if (this.issueList.length) {
            this.betNumIssue = String(this.issueList[0].betNumIssue)
          }
        } else {
          alert(res.msg || '期号查询失败，请稍后重试')
        }
      } catch (err) {
        console.error('期号查询失败：', err)
        alert('期号查询失败，请稍后重试')
      } finally {
        this.issueLoading = false
      }
    },

    // 期号下拉框里的开奖时间格式化："2026-10-05 21:30:00" -> "10-05 21:30"
    formatIssueTime(time) {
      if (!time) return ''
      const parts = String(time).split(' ')
      const date = parts[0] ? parts[0].slice(5) : ''    // 2026-10-05 -> 10-05
      const hm = parts[1] ? parts[1].slice(0, 5) : ''  // 21:30:00 -> 21:30
      return (date || '') + (hm ? ' ' + hm : '')
    },

    // 组合模式的文字说明，帮助新手理解每个选项的含义
    modeDesc(mode) {
      // 快乐8、七乐彩：选项就是选号个数
      if (this.lotteryType === 'klb' || this.lotteryType === 'qlc') {
        return `选 ${mode} 个号码`
      }
      // 排列三：玩法说明统一取自枚举表 plsModeDescMap
      if (this.lotteryType === 'pls') {
        return plsModeDescMap[mode] || mode
      }
      // 双色球 / 大乐透：模式形如 “6+1”，加号前是红/前区个数，加号后是蓝/后区个数
      if (mode.indexOf('+') > -1) {
        const parts = mode.split('+')
        if (this.lotteryType === 'ssq') {
          return `${parts[0]} 个红球 + ${parts[1]} 个蓝球`
        }
        if (this.lotteryType === 'dlt') {
          return `${parts[0]} 个前区 + ${parts[1]} 个后区`
        }
      }
      return ''
    },

    // 主号码：取投注号码对象里的 redNum，按逗号拆成数组
    mainNums(dto) {
      return (dto && dto.redNum ? dto.redNum : '').split(',').filter(Boolean)
    },
    // 辅助号码：取 blueNum（如双色球蓝球），没有则返回空数组
    extraNums(dto) {
      return (dto && dto.blueNum ? dto.blueNum : '').split(',').filter(Boolean)
    },

    // 校验一组号码：必须全是数字、在 min~max 范围内、默认不重复（可放开）；不合法直接弹窗提示
    // tokens：字符串形式的号码数组；max：允许的最大值；label：组名（用于提示语）
    // min：允许的最小值（排列三为 0）；allowRepeat：是否允许重复（排列三直选/组选3允许）
    validateNumGroup(tokens, max, label, min, allowRepeat) {
      if (min === undefined) min = 1
      if (!tokens.length) {
        alert(`请填写${label}号码`)
        return false
      }
      const used = []
      for (const token of tokens) {
        // 每一项都必须是纯数字（用户可能误填字母、符号）
        if (!/^\d+$/.test(token)) {
          alert(`${label}号码只能填写数字，“${token}”不合法`)
          return false
        }
        const n = Number(token)
        if (n < min || n > max) {
          alert(`${label}号码范围为 ${min}-${max}，“${n}”不合法`)
          return false
        }
        // 同一组内号码默认不能重复（排列三直选/组选3通过 allowRepeat 放开）
        if (!allowRepeat && used.indexOf(n) > -1) {
          alert(`${label}号码不能重复：${n}`)
          return false
        }
        used.push(n)
      }
      return true
    },

    // 把各个小方框里的号码校验并拼成后端要求的 betNumDTOs 数组
    // 拼接规则：redNum 主号码用英文逗号拼接；blueNum 辅助号码用英文逗号拼接，没有则为 null
    // 校验通过返回如 [{ redNum: '13,15,...,71', blueNum: null }]；不通过返回 null
    buildBetDTOs() {
      if (!this.mainArr.length) {
        alert('请先填写号码')
        return null
      }

      // 各彩票类型号码取值范围（max 值）
      const rangeMap = {
        klb: { main: 80 },
        ssq: { main: 33, extra: 16 },
        dlt: { main: 35, extra: 12 },
        qlc: { main: 30 },
        pls: { main: 9 } // 排列三：每位就是一个数字 0-9
      }
      const range = rangeMap[this.lotteryType] || { main: 99, extra: 99 }
      // 组名用于提示语
      const mainLabel = this.mainGroupLabel || ''
      const extraLabel = this.extraGroupLabel || ''

      // 取出方框内容（去首尾空格）
      const mainTokens = this.mainArr.map(v => (v || '').trim())
      const extraTokens = this.extraArr.map(v => (v || '').trim())

      // 1. 每个方框都必须填写
      if (mainTokens.some(v => !v)) {
        alert(`请把${mainLabel}号码全部填写完整`)
        return null
      }
      if (this.extraCount && extraTokens.some(v => !v)) {
        alert(`请把${extraLabel}号码全部填写完整`)
        return null
      }
      // 2. 数字、范围、重复校验（排列三最小 0；直选/组选3允许重复，组选6不允许）
      if (!this.validateNumGroup(mainTokens, range.main, mainLabel, this.numMin, this.allowRepeatNums)) return null
      if (this.extraCount && !this.validateNumGroup(extraTokens, range.extra, extraLabel)) return null

      // 3. 用英文逗号拼接成一个投注号码对象；没有辅助号码组时 blueNum 传 null
      const redNum = mainTokens.map(n => String(Number(n))).join(',')
      const blueNum = this.extraCount
        ? extraTokens.map(n => String(Number(n))).join(',')
        : null
      return [{ redNum, blueNum }]
    },

    // 点击“出号”/“提交”：先校验期号和手填号码，再请求投注接口，成功后弹窗展示
    async handleGenerate() {
      if (this.loading || !this.playType) return
      // 必须先选择购买期号
      if (!this.betNumIssue) {
        alert('请选择购买期号')
        return
      }

      // 手动方式：先校验用户输入并拼成 betNumDTOs，校验不通过直接中断
      let betNumDTOs = null
      if (this.isManual) {
        betNumDTOs = this.buildBetDTOs()
        if (!betNumDTOs) return
      }

      // 校验通过后二次确认，取消则不提交
      if (!confirm('是否确认出号？')) return

      this.loading = true
      try {
        // 公共参数：类型、玩法、号码来源、购买期号、开奖时间
        const params = {
          lotteryType: this.lotteryType,
          playType: this.playType,
          betNumSource: this.betNumSource,
          betNumIssue: this.betNumIssue,
          // 投注号码开奖时间：来自 getAllowBetNumInfo 返回的 drawNumTime
          betNumDrawTime: this.betNumDrawTime
        }
        // 手动方式额外带 betNumDTOs，如 [{ redNum: '13,15,...', blueNum: null }]
        // 随机方式不传，号码由后端机选
        if (this.isManual) {
          params.betNumDTOs = betNumDTOs
        }

        const res = await betNum(params)
        // 后端返回：{ code, success, data: { lotteryType, playType, betNumIssue, betNumSource, betNumDrawTime, betNumDTOs } }
        if (res.code === '200' || res.success) {
          // 弹窗回显信息全部取自返回结果（随机/手动均返回 betNumDTOs）
          this.result = res.data || null
          this.showResult = true
        } else {
          alert(res.msg || '提交失败，请稍后重试')
        }
      } catch (err) {
        console.error('提交失败：', err)
        alert('网络错误，请稍后重试')
      } finally {
        this.loading = false
      }
    },

    // 关闭结果弹窗
    closeResult() {
      this.showResult = false
    },

    // 复制弹窗中的号码到剪贴板
    // 格式规则：号码之间均用两个空格分隔
    // 1) 只有红球且不超过 5 个：单行，如 03  15  22
    // 2) 只有红球且超过 5 个：每行 5 个，超出部分换行
    // 3) 红球+蓝球：红球一行、蓝球一行
    async copyNums() {
      const dtos = this.result && this.result.betNumDTOs
      if (!dtos || !dtos.length) return
      const text = dtos
        .map(dto => {
          const main = this.mainNums(dto)
          const extra = this.extraNums(dto)
          // 红球+蓝球：红球一行、蓝球一行
          if (extra.length) {
            return [main.join('  '), extra.join('  ')].join('\n')
          }
          // 只有红球：超过 5 个则每行 5 个换行，否则单行
          if (main.length > 5) {
            const lines = []
            for (let i = 0; i < main.length; i += 5) {
              lines.push(main.slice(i, i + 5).join('  '))
            }
            return lines.join('\n')
          }
          return main.join('  ')
        })
        .join('\n')

      try {
        if (navigator.clipboard && window.isSecureContext) {
          // 优先使用剪贴板 API（需 https 或 localhost 环境）
          await navigator.clipboard.writeText(text)
        } else {
          // 兜底：临时 textarea + execCommand，兼容 http 环境
          const textarea = document.createElement('textarea')
          textarea.value = text
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'
          document.body.appendChild(textarea)
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }
        alert('号码已复制')
      } catch (err) {
        console.error('复制失败：', err)
        alert('复制失败，请手动复制')
      }
    },

    // 返回上一页（购买彩票选号方式页）
    goBack() {
      this.$router.back()
    }
  }
}
</script>

<style scoped>
/* 页面整体：与其他子页面一致的手机门户风格 */
.page {
  width: 100%;
  max-width: 480px;
  min-height: 100%;
  margin: 0 auto;
  background: #f5f6fa;
}

/* ===== 顶部导航栏 ===== */
.nav-bar {
  position: relative;
  height: 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-back {
  position: absolute;
  left: 10px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.nav-back svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: #fff;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.nav-title {
  color: #fff;
  font-size: 16px;
}

/* ===== 类型切换标签 ===== */
.type-tabs {
  display: flex;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
  /* 类型超过 4 个时横向滑动查看，一屏最多显示 4 个 */
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch; /* iOS 滑动惯性 */
  scrollbar-width: none; /* Firefox 隐藏滚动条 */
}

/* Chrome/Safari 隐藏滚动条但保留滑动能力 */
.type-tabs::-webkit-scrollbar {
  display: none;
}

.type-tab {
  /* 固定占 1/4 宽度，一屏正好 4 个；超出部分横向滑动，不被挤压 */
  flex: 0 0 25%;
  box-sizing: border-box;
  text-align: center;
  padding: 12px 4px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
}

/* 选中态：主题色文字 + 底部短横线 */
.type-tab.active {
  color: #667eea;
  font-weight: bold;
}

.type-tab.active::after {
  content: '';
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
  width: 28px;
  height: 3px;
  border-radius: 2px;
  background: #667eea;
}

/* ===== 选择区域 ===== */
.content {
  padding: 16px 12px;
}

.section {
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 24px;
}

.section-title {
  font-size: 15px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 14px;
}

/* ===== 模型推荐方式专属：待兑奖推荐号码区 ===== */
.model-rec-section {
  padding: 0 12px 16px;
}

/* 区块标题：与"选择区域"标题风格一致 */
.model-rec-title {
  font-size: 15px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 10px;
}

/* ===== 组合模式折叠框 ===== */
/* 标题行：标题在左、当前选择+箭头在右，整行可点 */
.collapse-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}

.collapse-head .section-title {
  margin-bottom: 0;
}

/* 右侧当前选择摘要：红色字 */
.collapse-sum {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #f56c6c;
  font-weight: bold;
  max-width: 60%;
}

/* 未选择时的灰色提示 */
.collapse-placeholder {
  color: #c0c4cc;
  font-weight: normal;
}

/* 折叠箭头：默认朝下，展开时旋转 180 度变朝上 */
.collapse-arrow {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  fill: none;
  stroke: #909399;
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-linejoin: round;
  transition: transform 0.25s ease;
}

.collapse-arrow.open {
  transform: rotate(180deg);
}

/* 折叠内容区：用 max-height 过渡做展开/收起动画 */
.collapse-body {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

/* 展开时：高度足够放下所有玩法选项（快乐8三行最高） */
.collapse-body.open {
  max-height: 500px;
}

/* 展开后内容与标题留出间距 */
.collapse-body.open .mode-grid,
.collapse-body.open .mode-empty {
  margin-top: 14px;
}

/* ===== 购买期号下拉框 ===== */
.issue-select-wrap {
  position: relative;
}

.issue-select {
  width: 100%;
  height: 44px;
  padding: 0 12px;
  border: 1.5px solid #dcdfe6;
  border-radius: 8px;
  font-size: 15px;
  color: #303133;
  background: #fff;
  outline: none;
  box-sizing: border-box;
  /* 去掉部分浏览器默认外观，统一风格 */
  appearance: none;
  -webkit-appearance: none;
  /* 右侧自定义下拉小箭头 */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23909399' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
}

/* 选中态/聚焦：红色描边，与页面购彩主题色一致 */
.issue-select:focus {
  border-color: #f56c6c;
}

.issue-select:disabled {
  background-color: #f5f7fa;
  color: #909399;
}

/* 暂无可购买期号提示 */
.issue-empty {
  margin-top: 10px;
  font-size: 12px;
  color: #e6a23c;
}

/* 组合模式按钮网格：自动换行，每行约 4 个 */
.mode-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.mode-item {
  width: calc((100% - 30px) / 4);
  border: 1.5px solid #dcdfe6;
  border-radius: 8px;
  padding: 8px 4px;
  text-align: center;
  cursor: pointer;
  box-sizing: border-box;
}

/* 选中态：红边红字（与购彩主题色一致） */
.mode-item.active {
  border-color: #f56c6c;
  background: #fef0f0;
}

.mode-value {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.mode-item.active .mode-value {
  color: #f56c6c;
}

.mode-desc {
  font-size: 10px;
  color: #909399;
  margin-top: 3px;
  line-height: 1.3;
}

/* 暂无组合模式提示 */
.mode-empty {
  text-align: center;
  color: #909399;
  font-size: 13px;
  padding: 30px 0;
}

/* ===== 手动填写号码（一个个独立小方框） ===== */
.boxes-panel {
  border: 1px solid #f0d0d0;
  border-radius: 10px;
  padding: 12px;
}

/* 组名（红球/蓝球、前区/后区） */
.group-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 8px;
}

/* 一行方框，放不下自动换行 */
.boxes-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* 单个号码方框：每个框只填一个号码 */
.num-box {
  width: 40px;
  height: 40px;
  border: 1.5px solid #dcdfe6;
  border-radius: 8px;
  text-align: center;
  font-size: 16px; /* 16px 防止手机（iPhone）聚焦时页面自动放大 */
  font-weight: bold;
  color: #303133;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

/* 主号码组：红色描边红字 */
.num-box.red {
  border-color: #f8a3a3;
  color: #f56c6c;
}
.num-box.red:focus {
  border-color: #f56c6c;
  box-shadow: 0 0 0 2px rgba(245, 108, 108, 0.15);
}

/* 辅助号码组：蓝色描边蓝字 */
.num-box.blue {
  border-color: #a0cfff;
  color: #409eff;
}
.num-box.blue:focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
}

/* 红蓝两组之间的分隔行：+ 号 + 组名 */
.extra-split {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0 8px;
}

.split-plus {
  font-size: 16px;
  font-weight: bold;
  color: #909399;
}

.extra-split .group-label {
  margin-bottom: 0;
}

/* 填写说明文字 */
.manual-tip {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.6;
  color: #909399;
}

/* 小屏手机：方框略缩小，保证一行能多放几个 */
@media screen and (max-width: 360px) {
  .num-box {
    width: 34px;
    height: 34px;
  }
  .boxes-row {
    gap: 6px;
  }
}

/* ===== 出号按钮 ===== */
.generate-btn {
  width: 100%;
  height: 46px;
  border: none;
  border-radius: 23px;
  background: linear-gradient(135deg, #f78a8a 0%, #f56c6c 100%);
  color: #fff;
  font-size: 17px;
  font-weight: bold;
  letter-spacing: 4px;
  cursor: pointer;
}

/* 禁用态：无组合模式可选或请求中 */
.generate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ===== 结果弹窗遮罩：铺满全屏，半透明黑色 ===== */
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  padding: 20px;
}

/* 弹窗主体：限制最大宽度，与门户风格一致，号码多时内部滚动 */
.modal-box {
  width: 100%;
  max-width: 420px;
  max-height: 80vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
}

.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.modal-close {
  font-size: 22px;
  line-height: 1;
  color: #909399;
  cursor: pointer;
  padding: 0 4px;
}

/* 选择信息回显区域 */
.modal-info {
  padding: 12px 16px 4px;
}

.info-row {
  display: flex;
  font-size: 13px;
  line-height: 2;
}

.info-label {
  width: 70px;
  color: #909399;
}

.info-value {
  color: #303133;
  font-weight: bold;
}

/* 号码区域 */
.modal-nums {
  padding: 8px 16px 14px;
}

.num-line {
  background: #fafafa;
  border-radius: 8px;
  padding: 10px 12px;
  margin-top: 8px;
}

.line-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 8px;
}

/* 随机机选成功提示（后端不返回机选号码） */
.random-tip {
  margin-top: 8px;
  padding: 14px 12px;
  border-radius: 8px;
  background: #fdf6ec;
  color: #e6a23c;
  font-size: 13px;
  line-height: 1.6;
  text-align: center;
}

/* 号码球：允许换行（快乐8一次有10个号码） */
.line-balls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.num-ball {
  min-width: 30px;
  height: 30px;
  padding: 0 4px;
  border-radius: 50%;
  background: #f56c6c; /* 主号码：红色球 */
  color: #fff;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.num-ball.blue {
  background: #409eff; /* 辅助号码：蓝色球 */
}

.num-plus {
  color: #909399;
  font-weight: bold;
}

/* 弹窗底部按钮 */
.modal-foot {
  display: flex;
  gap: 10px;
  padding: 0 16px 16px;
}

.foot-btn {
  flex: 1;
  height: 40px;
  border-radius: 20px;
  font-size: 15px;
  cursor: pointer;
}

.foot-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.foot-btn.plain {
  border: 1px solid #dcdfe6;
  background: #fff;
  color: #606266;
}

.foot-btn.primary {
  border: none;
  background: #f56c6c;
  color: #fff;
}

/* 手机小屏适配：组合模式说明文字再小一点，保证一行放得下 */
@media screen and (max-width: 360px) {
  .mode-desc {
    font-size: 9px;
  }
}
</style>
